import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getHistorial } from '../../services/muestrasApi';
import ExcelGrid from '../../components/muestras/ExcelGrid';
import { toast } from 'sonner';
import { 
  History, 
  Calendar, 
  TrendingUp,
  Table2,
  Users,
  Filter,
  Search,
  Eye,
  LogIn,
  Plus,
  Edit,
  Trash2,
  Upload,
  X,
  ArrowRight,
  FileJson
} from 'lucide-react';

// Helper para convertir fecha UTC a hora de Lima (UTC-5)
const toLimaTime = (dateString) => {
  if (!dateString) return null;
  // Crear fecha asumiendo que viene en UTC
  const utcDate = new Date(dateString + (dateString.includes('Z') || dateString.includes('+') ? '' : 'Z'));
  // Convertir a hora de Lima (UTC-5)
  const limaOffset = -5 * 60; // -5 horas en minutos
  const limaDate = new Date(utcDate.getTime() + limaOffset * 60 * 1000);
  return limaDate;
};

// Formatear fecha en hora de Lima
const formatLimaDateTime = (dateString, options = {}) => {
  const limaDate = toLimaTime(dateString);
  if (!limaDate) return '-';
  
  const defaultOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  };
  
  return limaDate.toLocaleString('es-PE', defaultOptions);
};

// Modal para mostrar detalles del movimiento
const DetalleModal = ({ movimiento, onClose }) => {
  if (!movimiento) return null;
  
  const parseJSON = (str) => {
    try {
      if (!str || str === 'null') return null;
      return JSON.parse(str);
    } catch {
      return str;
    }
  };
  
  const datosAnteriores = parseJSON(movimiento.datos_anteriores);
  const datosNuevos = parseJSON(movimiento.datos_nuevos);
  
  const renderData = (data, title, bgColor) => {
    if (!data) return null;
    
    return (
      <div className={`${bgColor} rounded-xl p-4`}>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
        {typeof data === 'object' ? (
          <div className="space-y-2">
            {Object.entries(data).map(([key, value]) => (
              <div key={key} className="flex justify-between items-start text-sm">
                <span className="text-gray-500 font-medium">{key}:</span>
                <span className="text-gray-800 text-right max-w-[60%] break-all">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-700">{String(data)}</p>
        )}
      </div>
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-xl">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600">
          <div className="flex items-center gap-3">
            <FileJson className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">Detalle del Movimiento</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Info básica */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Fecha y Hora (Lima)</p>
              <p className="font-medium text-gray-800">
                {formatLimaDateTime(movimiento.fecha_hora)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Usuario</p>
              <p className="font-medium text-gray-800">{movimiento.username || '-'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Tabla</p>
              <p className="font-medium text-gray-800">{movimiento.tabla || '-'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Acción</p>
              <p className="font-medium text-gray-800 capitalize">{movimiento.accion || '-'}</p>
            </div>
          </div>
          
          {/* Descripción */}
          {movimiento.descripcion && (
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="text-xs text-blue-600 font-medium mb-1">Descripción</p>
              <p className="text-gray-800">{movimiento.descripcion}</p>
            </div>
          )}
          
          {/* Datos antes y después */}
          {(datosAnteriores || datosNuevos) && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Cambios Realizados
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {renderData(datosAnteriores, 'Datos Anteriores', 'bg-red-50')}
                {renderData(datosNuevos, 'Datos Nuevos', 'bg-green-50')}
              </div>
            </div>
          )}
          
          {/* Info adicional */}
          {(movimiento.ip_address || movimiento.user_agent) && (
            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-gray-400 mb-2">Información técnica</p>
              <div className="flex gap-4 text-xs text-gray-500">
                {movimiento.ip_address && <span>IP: {movimiento.ip_address}</span>}
                {movimiento.user_agent && <span className="truncate max-w-xs">UA: {movimiento.user_agent}</span>}
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t bg-gray-50">
          <button 
            onClick={onClose}
            className="w-full py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const MuestrasHistorial = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    last7Days: 0,
    tablasAfectadas: 0,
    usuariosActivos: 0
  });
  const [filters, setFilters] = useState({ 
    usuario: '', 
    tabla: '', 
    accion: '',
    desde: '',
    hasta: ''
  });

  // Obtener tablas únicas de los datos
  const tablasUnicas = useMemo(() => {
    return [...new Set(data.map(h => h.tabla).filter(Boolean))].sort();
  }, [data]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const historialData = await getHistorial({});
      setData(historialData || []);
      
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const last7DaysCount = (historialData || []).filter(h => {
        const limaDate = toLimaTime(h.fecha_hora);
        return limaDate && limaDate >= sevenDaysAgo;
      }).length;
      const uniqueTables = new Set((historialData || []).map(h => h.tabla)).size;
      const uniqueUsers = new Set((historialData || []).map(h => h.username)).size;
      
      setStats({
        total: (historialData || []).length,
        last7Days: last7DaysCount,
        tablasAfectadas: uniqueTables,
        usuariosActivos: uniqueUsers
      });
    } catch (error) { 
      console.error('Error cargando historial:', error);
      toast.error('Error cargando historial'); 
    } finally { 
      setLoading(false); 
    }
  };

  const filteredData = useMemo(() => {
    let result = data;
    
    if (filters.usuario) {
      result = result.filter(h => h.username?.toLowerCase().includes(filters.usuario.toLowerCase()));
    }
    if (filters.tabla) {
      result = result.filter(h => h.tabla === filters.tabla);
    }
    if (filters.accion) {
      result = result.filter(h => h.accion?.toLowerCase().includes(filters.accion.toLowerCase()));
    }
    if (filters.desde) {
      const desdeDate = new Date(filters.desde + 'T00:00:00');
      result = result.filter(h => {
        const limaDate = toLimaTime(h.fecha_hora);
        return limaDate && limaDate >= desdeDate;
      });
    }
    if (filters.hasta) {
      const hastaDate = new Date(filters.hasta + 'T23:59:59');
      result = result.filter(h => {
        const limaDate = toLimaTime(h.fecha_hora);
        return limaDate && limaDate <= hastaDate;
      });
    }
    
    return result;
  }, [data, filters]);

  const clearFilters = () => {
    setFilters({ usuario: '', tabla: '', accion: '', desde: '', hasta: '' });
  };

  const hasFilters = filters.usuario || filters.tabla || filters.accion || filters.desde || filters.hasta;

  const getActionIcon = (accion) => {
    const a = accion?.toLowerCase() || '';
    if (a.includes('crear') || a.includes('create')) return <Plus className="w-3.5 h-3.5" />;
    if (a.includes('editar') || a.includes('update')) return <Edit className="w-3.5 h-3.5" />;
    if (a.includes('eliminar') || a.includes('delete')) return <Trash2 className="w-3.5 h-3.5" />;
    if (a.includes('login') || a.includes('sesion') || a.includes('sesión')) return <LogIn className="w-3.5 h-3.5" />;
    if (a.includes('subir') || a.includes('archivo') || a.includes('upload')) return <Upload className="w-3.5 h-3.5" />;
    return <Eye className="w-3.5 h-3.5" />;
  };

  const getActionStyle = (accion) => {
    const a = accion?.toLowerCase() || '';
    if (a.includes('crear') || a.includes('create')) return 'bg-emerald-100 text-emerald-700';
    if (a.includes('subir') || a.includes('archivo') || a.includes('upload')) return 'bg-sky-100 text-sky-700';
    if (a.includes('editar') || a.includes('update')) return 'bg-amber-100 text-amber-700';
    if (a.includes('eliminar') || a.includes('delete')) return 'bg-red-100 text-red-700';
    if (a.includes('login') || a.includes('sesion') || a.includes('sesión')) return 'bg-violet-100 text-violet-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getActionLabel = (accion) => {
    const a = accion?.toLowerCase() || '';
    if (a.includes('crear') || a.includes('create')) return 'Crear';
    if (a.includes('subir') || a.includes('archivo') || a.includes('upload')) return 'Subir';
    if (a.includes('editar') || a.includes('update')) return 'Editar';
    if (a.includes('eliminar') || a.includes('delete')) return 'Eliminar';
    if (a.includes('login') || a.includes('sesion') || a.includes('sesión')) return 'Login';
    return accion || '-';
  };

  const handleViewDetail = useCallback((movimiento) => {
    setSelectedMovimiento(movimiento);
  }, []);

  const columns = useMemo(() => [
    { 
      accessorKey: 'fecha_hora', 
      header: 'FECHA/HORA', 
      size: 150,
      cell: ({ row }) => {
        const fecha = row.original.fecha_hora;
        if (!fecha) return <span className="text-gray-400">-</span>;
        return (
          <span className="text-gray-600 text-sm">
            {formatLimaDateTime(fecha)}
          </span>
        );
      }
    },
    {
      accessorKey: 'username', 
      header: 'USUARIO', 
      size: 100,
      cell: ({ row }) => (
        <span className="font-medium text-gray-800">{row.original.username || '-'}</span>
      )
    },
    {
      accessorKey: 'accion', 
      header: 'ACCIÓN', 
      size: 110,
      cell: ({ row }) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${getActionStyle(row.original.accion)}`}>
          {getActionIcon(row.original.accion)}
          {getActionLabel(row.original.accion)}
        </span>
      )
    },
    {
      accessorKey: 'tabla', 
      header: 'TABLA', 
      size: 100,
      cell: ({ row }) => (
        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
          {row.original.tabla || '-'}
        </span>
      )
    },
    { 
      accessorKey: 'descripcion', 
      header: 'DESCRIPCIÓN', 
      size: 300, 
      cell: ({ row }) => (
        <span className="text-gray-600 text-sm line-clamp-2" title={row.original.descripcion}>
          {row.original.descripcion || '-'}
        </span>
      )
    },
    {
      id: 'detalles',
      header: 'VER',
      size: 60,
      cell: ({ row }) => (
        <button 
          onClick={() => handleViewDetail(row.original)}
          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title="Ver detalles"
        >
          <Eye className="w-4 h-4" />
        </button>
      )
    }
  ], [handleViewDetail]);

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-historial-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 bg-emerald-100 rounded-xl">
          <History className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">Historial de Movimientos</h1>
          <p className="text-gray-400 text-sm">Registro de todas las acciones en el sistema</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-violet-50 rounded-lg">
              <TrendingUp className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Total</p>
              <p className="text-xl font-bold text-gray-800">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 rounded-lg">
              <Calendar className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Últimos 7 días</p>
              <p className="text-xl font-bold text-gray-800">{stats.last7Days}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <Table2 className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Tablas</p>
              <p className="text-xl font-bold text-gray-800">{stats.tablasAfectadas}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-rose-50 rounded-lg">
              <Users className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Usuarios</p>
              <p className="text-xl font-bold text-gray-800">{stats.usuariosActivos}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-700 text-sm">Filtros</span>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto px-3 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded-lg border border-gray-200"
            >
              Limpiar
            </button>
          )}
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-40">
            <label className="block text-xs text-gray-400 mb-1">Usuario</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input
                type="text"
                value={filters.usuario}
                onChange={(e) => setFilters({ ...filters, usuario: e.target.value })}
                placeholder="Buscar..."
                className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="w-32">
            <label className="block text-xs text-gray-400 mb-1">Tabla</label>
            <select
              value={filters.tabla}
              onChange={(e) => setFilters({ ...filters, tabla: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">Todas</option>
              {tablasUnicas.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="w-32">
            <label className="block text-xs text-gray-400 mb-1">Acción</label>
            <select
              value={filters.accion}
              onChange={(e) => setFilters({ ...filters, accion: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">Todas</option>
              <option value="crear">Crear</option>
              <option value="editar">Editar</option>
              <option value="eliminar">Eliminar</option>
              <option value="login">Login</option>
              <option value="subir">Subir</option>
            </select>
          </div>
          <div className="w-44">
            <label className="block text-xs text-gray-400 mb-1">Desde</label>
            <input
              type="date"
              value={filters.desde}
              onChange={(e) => setFilters({ ...filters, desde: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="w-44">
            <label className="block text-xs text-gray-400 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.hasta}
              onChange={(e) => setFilters({ ...filters, hasta: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <ExcelGrid data={filteredData} columns={columns} loading={loading} pageSize={30} />
      </div>
      
      {/* Modal de detalle */}
      {selectedMovimiento && (
        <DetalleModal 
          movimiento={selectedMovimiento} 
          onClose={() => setSelectedMovimiento(null)} 
        />
      )}
    </div>
  );
};

export default MuestrasHistorial;
